// src/components/home/Roadmap.jsx
import React, { useState } from "react";
import {
  FaCheck,
  FaSpinner,
  FaCircle,
  FaCalendarAlt,
  FaGraduationCap,
  FaChalkboardTeacher,
  FaCode,
  FaGlobe,
  FaRocket,
  FaBolt,
  FaBrain,
  FaTerminal,
  FaChartLine,
  FaEye,
  FaLaptopCode,
  FaUserShield,
  FaProductHunt,
  FaRobot,
  FaCogs,
  FaSlidersH,
  FaComments,
  FaLightbulb,
  FaChartBar,
  FaCertificate,
  FaMobileAlt,
  FaLayerGroup
} from "react-icons/fa";
import "./Roadmap.css";

const roadmapItems = [
  {
    phase: "Phase 1 • Q3–Q4 2025",
    category: "students",
    status: "completed",
    badgeText: "Completed",
    icon: <FaCheck className="bvh-roadmap-icon" />,
    titleIcon: <FaRocket className="bvh-title-icon" />,
    title: "BodhAI V1 Launch",
    desc: "Built the first production-ready version of BodhAI featuring AI-powered MCQ practice, coding assessments, teacher monitoring, and student analytics.",
    highlights: [
      { text: "AI MCQ Engine", icon: <FaBrain /> },
      { text: "Judge0 Coding Runner", icon: <FaTerminal /> },
      { text: "Student Analytics Dashboard", icon: <FaChartLine /> },
      { text: "Teacher Live Monitoring", icon: <FaEye /> }
    ],
    tags: ["#BodhAIV1", "#MCQPractice", "#Judge0Runner", "#StudentDashboard"]
  },
  {
    phase: "Phase 2 • Q2–Q3 2026",
    category: "teachers",
    status: "completed",
    badgeText: "Live Beta",
    icon: <FaRocket className="bvh-roadmap-icon" />,
    titleIcon: <FaBolt className="bvh-title-icon" />,
    title: "BodhAI V2 Beta & Product Hunt",
    desc: "A complete UI/UX redesign introducing faster assessment loading, teacher proctoring workflows, improved performance, and public Product Hunt launch.",
    highlights: [
      { text: "Modern Web Architecture", icon: <FaLaptopCode /> },
      { text: "Teacher Proctoring Suite", icon: <FaUserShield /> },
      { text: "Live Assessment Monitoring", icon: <FaEye /> },
      { text: "Product Hunt Launch", icon: <FaProductHunt /> }
    ],
    tags: ["#V2Beta", "#TeacherPortal", "#LiveMonitoring", "#ProductHunt"]
  },
  {
    phase: "Phase 3 • Q3 2026",
    category: "developers",
    status: "current",
    badgeText: "In Progress",
    icon: <FaSpinner className="bvh-roadmap-icon bvh-spin" />,
    titleIcon: <FaRobot className="bvh-title-icon" />,
    title: "AI Question Generator & Compiler Tuning",
    desc: "Enhancing question generation models, optimizing Judge0 compiler execution latency, and integrating community feedback.",
    highlights: [
      { text: "AI Question Generators", icon: <FaRobot /> },
      { text: "Judge0 Compiler Optimization", icon: <FaCogs /> },
      { text: "Smart MCQ Difficulty", icon: <FaSlidersH /> },
      { text: "Community Feedback Integration", icon: <FaComments /> }
    ],
    tags: ["#AIQuestionGen", "#CompilerOptimization", "#SmartAssessments"]
  },
  {
    phase: "Phase 4 • Q4 2026",
    category: "teachers",
    status: "upcoming",
    badgeText: "Upcoming",
    icon: <FaCircle className="bvh-roadmap-icon small" />,
    titleIcon: <FaGraduationCap className="bvh-title-icon" />,
    title: "AI Tutor & Adaptive Learning",
    desc: "Personalized AI explanations for missed coding test cases, step-by-step guidance, and automated difficulty scaling.",
    highlights: [
      { text: "AI Tutor Explanation Engine", icon: <FaLightbulb /> },
      { text: "Adaptive Difficulty Scaling", icon: <FaChartBar /> },
      { text: "Personalized Test Recommendations", icon: <FaBrain /> },
      { text: "Automated Certificate System", icon: <FaCertificate /> }
    ],
    tags: ["#AITutor", "#AdaptiveLearning", "#SmartExplanations"]
  },
  {
    phase: "Phase 5 • 2027+",
    category: "ecosystem",
    status: "upcoming",
    badgeText: "Vision",
    icon: <FaGlobe className="bvh-roadmap-icon" />,
    titleIcon: <FaGlobe className="bvh-title-icon" />,
    title: "Mobile App & BodhAI v2.0",
    desc: "Expanding BodhAI into a complete education ecosystem spanning cross-platform mobile apps and global classroom support.",
    highlights: [
      { text: "Native Mobile Proctoring Apps", icon: <FaMobileAlt /> },
      { text: "Complete Platform Ecosystem", icon: <FaLayerGroup /> },
      { text: "Global Classroom Tools", icon: <FaGlobe /> },
      { text: "Developer API Extensions", icon: <FaCode /> }
    ],
    tags: ["#MobileApps", "#BodhAIv2", "#GlobalEducation"]
  }
];

const Roadmap = () => {
  const [activeTab, setActiveTab] = useState("all");

  const filteredItems =
    activeTab === "all"
      ? roadmapItems
      : roadmapItems.filter(
          (item) => item.category === activeTab || item.status === "completed"
        );

  return (
    <section className="bvh-roadmap-section" data-aos="fade-up">
      <div className="bvh-roadmap-header">
        <span className="bvh-roadmap-pill">
          <FaCalendarAlt /> Official Product Roadmap
        </span>
        <h2 className="bvh-roadmap-title">Where We're Headed</h2>
        <p className="bvh-roadmap-subtitle">
          Real development milestones across the BodhAI platform ecosystem.
        </p>

        {/* Category Filter Tabs */}
        <div className="bvh-roadmap-tabs">
          <button
            className={`bvh-roadmap-tab ${activeTab === "all" ? "active" : ""}`}
            onClick={() => setActiveTab("all")}
          >
            All Milestones
          </button>
          <button
            className={`bvh-roadmap-tab ${activeTab === "students" ? "active" : ""}`}
            onClick={() => setActiveTab("students")}
          >
            <FaGraduationCap /> Students
          </button>
          <button
            className={`bvh-roadmap-tab ${activeTab === "teachers" ? "active" : ""}`}
            onClick={() => setActiveTab("teachers")}
          >
            <FaChalkboardTeacher /> Teachers & Schools
          </button>
          <button
            className={`bvh-roadmap-tab ${activeTab === "developers" ? "active" : ""}`}
            onClick={() => setActiveTab("developers")}
          >
            <FaCode /> Developers
          </button>
          <button
            className={`bvh-roadmap-tab ${activeTab === "ecosystem" ? "active" : ""}`}
            onClick={() => setActiveTab("ecosystem")}
          >
            <FaGlobe /> Ecosystem
          </button>
        </div>
      </div>

      <div className="bvh-roadmap-grid">
        {filteredItems.map((item, index) => (
          <div className={`bvh-roadmap-box ${item.status}`} key={index}>
            <div className="bvh-roadmap-box-header">
              <span className="bvh-roadmap-phase">{item.phase}</span>
              <span className="bvh-roadmap-status-badge">
                {item.icon} {item.badgeText}
              </span>
            </div>
            <h3 className="bvh-roadmap-box-title">
              {item.titleIcon} {item.title}
            </h3>
            <p className="bvh-roadmap-box-desc">{item.desc}</p>

            {item.highlights && (
              <div className="bvh-roadmap-highlights-box">
                <span className="bvh-highlights-label">Key Highlights</span>
                <ul className="bvh-highlights-list">
                  {item.highlights.map((hl, hIdx) => (
                    <li key={hIdx}>
                      <span className="bvh-hl-icon">{hl.icon}</span>
                      {hl.text}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="bvh-roadmap-tags">
              {item.tags.map((tag, tIdx) => (
                <span className="bvh-roadmap-tag" key={tIdx}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default React.memo(Roadmap);
